import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, Message, PracticeSession, UserLangProgress, Vocab, OralCorrection, Tutor, PracticeVarsOption, ExamVarsOption, ExamSession, PracticeResponseRecommendation, ConversationContextUpdate, ChatMetrics, ExamStageMetrics, TodoList, TodoItem, UserContactInfo, InventoryItem, ChatEmotion } from '@/components/Types';
import { UI_LOCALE_LANGUAGES } from '@/lib/constants';
import { setCookie } from 'cookies-next';
import { isBlank } from '@/lib/utils';
import { ControlledState, Status } from '@11labs/react';
import { HookOptions } from '@11labs/react';
import { LearningArticle } from '@/lib/types/articles';
import { NpcCharacter } from '@/lib/types/story';

// Helper function to get initial locale from cookies
const getInitialLocale = (): keyof typeof UI_LOCALE_LANGUAGES => {
  if (typeof window === 'undefined') {
    // Server-side: get from headers
    const headers = typeof window === 'undefined' ? require('next/headers') : null;
    const locale = headers?.headers().get('x-user-locale');
    return (locale as keyof typeof UI_LOCALE_LANGUAGES) || 'english';
  }

  // Client-side: get from cookies
  const cookies = require('cookies-next');
  const locale = cookies.getCookie('locale');

  if (isBlank(locale) || locale == "null" || locale == "undefined") {
    return 'english';
  } else {
    return (locale as keyof typeof UI_LOCALE_LANGUAGES) || 'english';
  }
};

export interface HeaderTab {
  id: string;
  active: boolean;
  to: string;
  label?: string;
  showDot?: boolean;
}

export interface AppState {
  isMobileScreen: boolean;
  locale: keyof typeof UI_LOCALE_LANGUAGES;
  currentUser: User | null;
  learningLanguage: string;

  // Language progress object is a user's "profile data" for learned language
  languageProgress: UserLangProgress | null;

  achievementRewards: Partial<InventoryItem>[];

  featureFlags: {
    enableNewUi: boolean;
  };

  // Audio devices to be used for voice chats
  selectedInputDevice: string | null;
  selectedOutputDevice: string | null;

  isRecordingSpeech: boolean;

  userSessionInitialized: boolean;

  // User-scope vocab and corrections (actually taken from the language progress)
  currentVocab: Vocab[];
  learnedVocab: Vocab[];
  dismissedVocab: string[];
  currentCorrections: OralCorrection[];
  dismissedCorrections: OralCorrection[];
  currentChatMetrics: ChatMetrics | null;
  currentExamMetrics: ExamStageMetrics | null;

  // Technical and session data for an active voice chat session
  currentConversationId?: string | null;
  currentConversationIsSpeaking?: boolean | null;
  currentPracticeId?: string | null;
  currentPractice?: PracticeSession | null;
  currentPracticeUserGoal?: string | null;
  currentPracticeUserGoalId?: string | null;
  currentExamId?: string | null;
  currentExam?: ExamSession | null;
  currentTutor?: Tutor | null;
  currentCharacter?: Partial<NpcCharacter> | null;
  currentPracticeCustomization?: PracticeVarsOption | null;
  currentExamCustomization?: ExamVarsOption | null;
  currentLearningArticleId?: string | null;
  currentLearningArticle?: Partial<LearningArticle> | null;

  pendingPracticeStart: { goalId: string | null } | null; 

  headerTabs: HeaderTab[];

  // Contacts
  showAddingContactModal: boolean;
  showNewEmotionModal: boolean;
  addingContact: UserContactInfo | null;
  lastAddedContact: UserContactInfo | null;
  lastAddedEmotion: ChatEmotion | null;

  // Tracking active chat session vocab and corrections
  currentSessionMessages: Message[];
  currentSessionVocab: Vocab[];
  currentSessionOralCorrections: OralCorrection[];
  currentSessionContextUpdates: ConversationContextUpdate[];
  currentSessionPendingUserMessages: Message[];
  currentSessionMessageXpAwards: Record<string, number>;

  // Practice session response recommendations
  recommendResponses: boolean;
  practiceResponseRecommendations: PracticeResponseRecommendation[];
  previousPracticeResponseRecommendations: PracticeResponseRecommendation[];

  // Todo lists
  todoLists: Record<string, TodoList>;
  newTodoItemCreated: TodoItem | null;
  selectedTodoItem: TodoItem | null;

  // XP management
  progressLevelUpdatePending: boolean;
  levelUpdateJustHappened: boolean;

  isFlutterWebview: boolean;

  isBottomNavVisible: boolean;
  hasPageFloatingBar: boolean;
}

const initialState: AppState = {
  currentUser: null,
  locale: getInitialLocale(),
  languageProgress: null,
  learningLanguage: 'spanish',
  userSessionInitialized: false,
  achievementRewards: [],
  currentCharacter: null,
  currentConversationId: null,
  currentConversationIsSpeaking: false,
  currentExamId: null,
  currentExam: null,
  currentPracticeId: 'test',
  currentPractice: null,
  currentPracticeCustomization: null,
  currentSessionMessages: [],
  currentSessionVocab: [],
  currentSessionOralCorrections: [],
  currentSessionContextUpdates: [],
  currentSessionPendingUserMessages: [],
  currentSessionMessageXpAwards: {},
  currentChatMetrics: null,
  currentExamMetrics: null,
  currentLearningArticleId: null,
  currentLearningArticle: null,
  selectedInputDevice: null,
  selectedOutputDevice: null,
  pendingPracticeStart: null,
  isMobileScreen: false,
  isRecordingSpeech: false,
  currentVocab: [],
  learnedVocab: [],
  dismissedVocab: [],
  currentCorrections: [],
  dismissedCorrections: [],
  currentTutor: null,
  practiceResponseRecommendations: [],
  previousPracticeResponseRecommendations: [],
  recommendResponses: true,
  todoLists: {},
  newTodoItemCreated: null,
  selectedTodoItem: null,
  showAddingContactModal: false,
  showNewEmotionModal: false,
  addingContact: null,
  lastAddedContact: null,
  lastAddedEmotion: null,
  progressLevelUpdatePending: false,
  levelUpdateJustHappened: false,
  isFlutterWebview: typeof window !== 'undefined' && (window as any).isFlutterWebview !== undefined && (window as any).isFlutterWebview,
  isBottomNavVisible: false,
  hasPageFloatingBar: false,
  featureFlags: {
    enableNewUi: true,
  },
  headerTabs: [    
    {
      id: 'recent',
      active: false,
      to: '/learning-feed/recent',
      label: 'recent',
    },
    {
      id: 'games',
      active: false,
      to: '/learning-feed/games',
      label: 'games',
      showDot: false,
    },
    {
      id: 'feed',
      active: false,
      to: '/learning-feed',
      label: 'feed',
    },
  ],
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {        
    setCurrentUser: (state, action: PayloadAction<User>) => {
      if (state.currentUser?.motherLanguage != action.payload.motherLanguage) {
        // setCookie('locale', action.payload.motherLanguage);
      }
      state.currentUser = action.payload;
    },    
    setLearningLanguage: (state, action: PayloadAction<string>) => {
      state.learningLanguage = action.payload;
    },
    setCurrentConversationId: (state, action: PayloadAction<string>) => {
      state.currentConversationId = action.payload;
    },
    setCurrentConversationIsSpeaking: (state, action: PayloadAction<boolean>) => {
      state.currentConversationIsSpeaking = action.payload;
    },
    setCurrentExamId: (state, action: PayloadAction<string | null>) => {
      state.currentExamId = action.payload;
    },
    setCurrentExam: (state, action: PayloadAction<ExamSession | null>) => {
      state.currentExam = action.payload;
      state.currentExamId = action.payload?._id ?? null;
    },
    setCurrentPracticeId: (state, action: PayloadAction<string | null>) => {
      state.currentPracticeId = action.payload;
    },
    setCurrentPractice: (state, action: PayloadAction<PracticeSession | null>) => {
      state.currentPractice = action.payload;
    },
    setCurrentPracticeCustomization: (state, action: PayloadAction<PracticeVarsOption | null>) => {
      state.currentPracticeCustomization = action.payload;
    },
    setCurrentPracticeUserGoal: (state, action: PayloadAction<{ goal: string, goalId: string } | null>) => {
      state.currentPracticeUserGoal = action.payload?.goal ?? null;
      state.currentPracticeUserGoalId = action.payload?.goalId ?? null;
    },
    setLanguageProgress: (state, action: PayloadAction<UserLangProgress>) => {
      state.languageProgress = action.payload;
      state.learningLanguage = action.payload.language;      

      // Special case for the new UI
      if (['spanish', 'german'].includes(action.payload.language)) {
        state.featureFlags.enableNewUi = true;
      } else {
        state.featureFlags.enableNewUi = false;
      }
    },
    updateLanguageProgressStats: (state, action: PayloadAction<{ experience?: number, rankLevel?: number, experienceTillNextLevel?: number }>) => {
      if (state.languageProgress) {
        state.languageProgress = {
          ...state.languageProgress,
          experience: action.payload.experience ?? state.languageProgress.experience,
          rankLevel: action.payload.rankLevel ?? state.languageProgress.rankLevel,
          experienceTillNextLevel: action.payload.experienceTillNextLevel ?? state.languageProgress.experienceTillNextLevel,
        };
      }
    },
    setUserSessionInitialized: (state, action: PayloadAction<boolean>) => {
      state.userSessionInitialized = action.payload;
    },
    setCurrentChatMetrics: (state, action: PayloadAction<ChatMetrics>) => {
      state.currentChatMetrics = action.payload;
    },    
    addCurrentChatMetricsXp: (state, action: PayloadAction<number>) => {
      if (state.currentChatMetrics) {
        state.currentChatMetrics = {
          ...state.currentChatMetrics,
          xpGained: state.currentChatMetrics.xpGained + action.payload,
        };
      } else {
        state.currentChatMetrics = {
          wpm: 0,
          xpGained: action.payload,
          numMistakes: 0,
        };
      }
    },
    addCurrentChatMetricsMistakes: (state, action: PayloadAction<number>) => {
      if (state.currentChatMetrics) {
        state.currentChatMetrics = {
          ...state.currentChatMetrics,
          numMistakes: state.currentChatMetrics.numMistakes + action.payload,
        };
      } else {
        state.currentChatMetrics = {
          wpm: 0,
          xpGained: 0,
          numMistakes: action.payload,
        };
      }
    },
    setCurrentChatMetricsWpm: (state, action: PayloadAction<number>) => {
      if (state.currentChatMetrics) {
        state.currentChatMetrics = {
          ...state.currentChatMetrics,
          wpm: action.payload,
        };
      } else {
        state.currentChatMetrics = {
          wpm: action.payload,
          xpGained: 0,
          numMistakes: 0,
        };
      }
    },
    setCurrentExamMetrics: (state, action: PayloadAction<ExamStageMetrics>) => {
      state.currentExamMetrics = action.payload;
    },
    clearSessionMessages: (state) => {      
      state.currentSessionMessages = [];
      state.currentSessionVocab = [];
      state.currentSessionOralCorrections = [];
      state.currentSessionContextUpdates = [];
      state.currentSessionPendingUserMessages = [];
      state.currentSessionMessageXpAwards = {};
      state.currentChatMetrics = null;
      state.currentExamMetrics = null;
    },
    addSessionMessage: (state, action: PayloadAction<Message>) => {
      state.currentSessionMessages.push(action.payload);
    },
    setSessionMessages: (state, action: PayloadAction<Message[]>) => {
      state.currentSessionMessages = action.payload;
    },
    setHistoryPracticeSession: (state, action: PayloadAction<PracticeSession>) => {      
      state.currentSessionMessages = action.payload.messages ?? [];
      state.currentSessionVocab = action.payload.vocab ?? [];
      state.currentSessionOralCorrections = action.payload.corrections ?? [];
    },
    setSelectedInputDevice: (state, action: PayloadAction<string>) => {
      state.selectedInputDevice = action.payload;
    },
    setSelectedOutputDevice: (state, action: PayloadAction<string>) => {
      state.selectedOutputDevice = action.payload;
    },
    setLocale: (state, action: PayloadAction<keyof typeof UI_LOCALE_LANGUAGES>) => {
      if (state.locale != action.payload) {
        setCookie('locale', action.payload, { maxAge: 60 * 60 * 24 * 30 * 6 });
      }
      state.locale = action.payload;
    },
    setIsMobileScreen: (state, action: PayloadAction<boolean>) => {
      state.isMobileScreen = action.payload;
    },
    setSessionVocab: (state, action: PayloadAction<Vocab[]>) => {
      state.currentSessionVocab = action.payload;
    },
    setSessionOralCorrections: (state, action: PayloadAction<OralCorrection[]>) => {
      state.currentSessionOralCorrections = action.payload;
    },
    setVocab: (state, action: PayloadAction<{ current: Vocab[], learned: Vocab[], dismissed: string[] } | null>) => {
      if (action.payload != null) {
        state.currentVocab = action.payload.current;
        state.learnedVocab = action.payload.learned;
        state.dismissedVocab = action.payload.dismissed;
      }
    },
    setCorrections: (state, action: PayloadAction<{ current: OralCorrection[], dismissed: OralCorrection[] } | null | undefined>) => {
      if (action.payload != null) {
        state.currentCorrections = action.payload.current;
        state.dismissedCorrections = action.payload.dismissed;
      }
    },
    setCurrentTutor: (state, action: PayloadAction<Tutor>) => {
      state.currentTutor = action.payload;
    },
    setCurrentCharacter: (state, action: PayloadAction<Partial<NpcCharacter>>) => {
      state.currentCharacter = action.payload;
    },
    setIsRecordingSpeech: (state, action: PayloadAction<boolean>) => {
      state.isRecordingSpeech = action.payload;
    },
    clearPracticeResponseRecommendations: (state) => {
      state.practiceResponseRecommendations = [];
      state.previousPracticeResponseRecommendations = [];
    },
    setPracticeResponseRecommendations: (state, action: PayloadAction<PracticeResponseRecommendation[]>) => {
      state.previousPracticeResponseRecommendations = [...state.practiceResponseRecommendations];
      state.practiceResponseRecommendations = action.payload;
    },
    setRecommendResponses: (state, action: PayloadAction<boolean>) => {
      state.recommendResponses = action.payload;
    },
    addSessionContextUpdate: (state, action: PayloadAction<ConversationContextUpdate>) => {
      state.currentSessionContextUpdates.push(action.payload);
    },
    clearSessionContextUpdates: (state) => {
      state.currentSessionContextUpdates = [];
    },
    addSessionPendingUserMessage: (state, action: PayloadAction<Message>) => {
      state.currentSessionPendingUserMessages.push(action.payload);
    },
    clearSessionPendingUserMessages: (state) => {
      state.currentSessionPendingUserMessages = [];
    },
    addSessionMessageXpAward: (state, action: PayloadAction<{ message: Message, xpGranted: number}>) => {
      state.currentSessionMessageXpAwards[action.payload.message.message] = action.payload.xpGranted;

      // Also, find this message in the current session messages and update the xp
      const messageIndex = state.currentSessionMessages.findIndex(m => m.message === action.payload.message.message);
      if (messageIndex != -1) {
        state.currentSessionMessages[messageIndex].xp = action.payload.xpGranted;
      }
    },
    setPendingPracticeStart: (state, action: PayloadAction<{ goalId: string | null } | null>) => {
      state.pendingPracticeStart = action.payload;
    },
    setTodoLists: (state, action: PayloadAction<Record<string, TodoList>>) => {
      state.todoLists = action.payload;
    },
    setTodoList: (state, action: PayloadAction<{ tag: string, todoList: TodoList }>) => {
      state.todoLists = {
        ...state.todoLists,
        [action.payload.tag]: action.payload.todoList,
      };
    },
    setSelectedTodoItem: (state, action: PayloadAction<TodoItem | null>) => {
      state.selectedTodoItem = action.payload;
    },
    setNewTodoItemCreated: (state, action: PayloadAction<TodoItem | null>) => {
      state.newTodoItemCreated = action.payload;
    },
    setCurrentLearningArticleId: (state, action: PayloadAction<string | null>) => {
      state.currentLearningArticleId = action.payload;
    },
    setCurrentLearningArticle: (state, action: PayloadAction<Partial<LearningArticle> | null>) => {
      state.currentLearningArticle = action.payload;
    },
    setHeaderTabs: (state, action: PayloadAction<HeaderTab[]>) => {
      state.headerTabs = action.payload;
    },
    setActiveHeaderTab: (state, action: PayloadAction<string>) => {
      state.headerTabs = state.headerTabs.map(tab => ({ ...tab, active: tab.id === action.payload }));      
      
      // Change the showDot flag for the new active tab to false
      state.headerTabs = state.headerTabs.map(tab => ({ ...tab, showDot: tab.id === action.payload ? false : tab.showDot }));
    },
    setShowDotInHeaderTab: (state, action: PayloadAction<{ tabId: string, showDot: boolean }>) => {
      state.headerTabs = state.headerTabs.map(tab => ({ ...tab, showDot: tab.id === action.payload.tabId ? action.payload.showDot : tab.showDot }));
    },
    setShowAddingContactModal: (state, action: PayloadAction<{ show: boolean, contact: UserContactInfo | null }>) => {
      state.showAddingContactModal = action.payload.show;
      state.addingContact = action.payload.contact;
      if (action.payload.contact) { 
        state.lastAddedContact = action.payload.contact;
      }
    },    
    setShowNewEmotionModal: (state, action: PayloadAction<{ show: boolean, emotion?: ChatEmotion | null }>) => {
      state.showNewEmotionModal = action.payload.show;

      if (action.payload.emotion) { 
        state.lastAddedEmotion = action.payload.emotion;
      }
    },
    setLastAddedContact: (state, action: PayloadAction<UserContactInfo | null>) => {
      state.lastAddedContact = action.payload;
    },
    setEnableNewUi: (state, action: PayloadAction<boolean>) => {
      state.featureFlags.enableNewUi = action.payload;
    },
    setProgressLevelUpdatePending: (state, action: PayloadAction<boolean>) => {
      state.progressLevelUpdatePending = action.payload;
    },
    setLevelUpdateJustHappened: (state, action: PayloadAction<boolean>) => {
      state.levelUpdateJustHappened = action.payload;
    },
    changeGameLives: (state, action: PayloadAction<number>) => {
      if (state.languageProgress) {
        state.languageProgress = {
          ...state.languageProgress,
          gameLives: state.languageProgress.gameLives + action.payload,
        };
      }
    },
    setGameLives: (state, action: PayloadAction<number>) => {
      if (state.languageProgress) {
        state.languageProgress = {
          ...state.languageProgress,
          gameLives: action.payload,
        };
      }
    },
    setAchievementRewards: (state, action: PayloadAction<Partial<InventoryItem>[]>) => {
      state.achievementRewards = action.payload;
    },
    addAchievementReward: (state, action: PayloadAction<Partial<InventoryItem>>) => {
      state.achievementRewards.push(action.payload);
    },
    setIsFlutterWebview: (state, action: PayloadAction<boolean>) => {
      state.isFlutterWebview = action.payload;
    },
    setIsBottomNavVisible: (state, action: PayloadAction<boolean>) => {
      state.isBottomNavVisible = action.payload;
    },
    setHasPageFloatingBar: (state, action: PayloadAction<boolean>) => {
      state.hasPageFloatingBar = action.payload;
    },
  },
});

export const {
  setCurrentUser,
  setLearningLanguage,
  setCurrentConversationId,
  setCurrentConversationIsSpeaking,
  setLanguageProgress,
  updateLanguageProgressStats,
  setUserSessionInitialized,
  setCurrentPracticeId,
  setCurrentPractice,
  setCurrentExamId,
  setCurrentExam,
  setCurrentPracticeCustomization,
  setCurrentPracticeUserGoal,
  clearSessionMessages,
  addSessionMessage,
  setSessionMessages,
  addSessionContextUpdate,
  clearSessionContextUpdates,
  addSessionPendingUserMessage,
  clearSessionPendingUserMessages,
  setSelectedInputDevice,
  setSelectedOutputDevice,
  setIsMobileScreen,
  setSessionVocab,
  setSessionOralCorrections,
  setVocab,
  setCorrections,
  setCurrentTutor,
  setCurrentCharacter,
  setLocale,
  clearPracticeResponseRecommendations,
  setPracticeResponseRecommendations,
  setRecommendResponses,
  setHistoryPracticeSession,
  addSessionMessageXpAward,
  setCurrentChatMetrics,
  addCurrentChatMetricsXp,
  addCurrentChatMetricsMistakes,
  setCurrentChatMetricsWpm,
  setCurrentExamMetrics,
  setPendingPracticeStart,
  setIsRecordingSpeech,
  setTodoLists,
  setTodoList,
  setSelectedTodoItem,
  setNewTodoItemCreated,
  setCurrentLearningArticleId,
  setCurrentLearningArticle,
  setHeaderTabs,
  setActiveHeaderTab,
  setShowDotInHeaderTab,
  setShowAddingContactModal,
  setLastAddedContact,
  setEnableNewUi,
  setProgressLevelUpdatePending,
  setLevelUpdateJustHappened,
  changeGameLives,
  setGameLives,
  setAchievementRewards,
  addAchievementReward,
  setShowNewEmotionModal,
  setIsFlutterWebview,
  setIsBottomNavVisible,
  setHasPageFloatingBar,
} = appSlice.actions;

export default appSlice.reducer; 